import { useMemo } from 'react';
import type { ColumnsType } from 'antd/es/table';
import { getColumnWidth } from '../config/columnWidths';
import type { InternalScenarioRecord, ScenarioManagerColumn } from '../types';
import { createColumnRenderers, renderCellValue } from './createColumnRenderers';
import type { ColumnRenderersContext } from './createColumnRenderers';

const SCENARIO_HIDDEN_COLUMN_IDS = new Set(['simulation_period', 'scope']);

type BuildScenarioColumnsParams = ColumnRenderersContext & {
    columnsConfig: ScenarioManagerColumn[];
};

export const useScenarioColumns = ({
    columnsConfig,
    isProjectExpanded,
    showAnalyticsCheckbox,
    onToggleExpansion,
    onScenarioClick,
    getActionMenuItems,
    handleRowAction,
}: BuildScenarioColumnsParams): ColumnsType<InternalScenarioRecord> => {
    const columnRenderers = useMemo(
        () =>
            createColumnRenderers({
                isProjectExpanded,
                showAnalyticsCheckbox,
                onToggleExpansion,
                onScenarioClick,
                getActionMenuItems,
                handleRowAction,
            }),
        [
            getActionMenuItems,
            handleRowAction,
            isProjectExpanded,
            onScenarioClick,
            onToggleExpansion,
            showAnalyticsCheckbox,
        ],
    );

    return useMemo(
        () =>
            columnsConfig
                .filter((column) => column.is_default)
                .map((column, index) => ({
                    title: column.label || column.id || '-',
                    key: `scenario-${column.id || `column-${index}`}`,
                    dataIndex: column.id,
                    width: getColumnWidth(column.id),
                    render: (_value: unknown, record: InternalScenarioRecord) =>
                        SCENARIO_HIDDEN_COLUMN_IDS.has(column.id ?? '')
                            ? null
                            : renderCellValue(column.id, record, columnRenderers),
                })),
        [columnsConfig, columnRenderers],
    );
};
